/* eslint-disable @typescript-eslint/naming-convention */
// eslint-disable react/no-danger
/*
 * Helpers for Control Center Locale translations
 * Handled ?lang=fr_CA on the URL to set the locale
 */
import i18next from "i18next";
import { Locale as LocaleBase } from "@dealer-center/core-labels";

type LocaleValue = string | { [key: string]: LocaleValue };
type LocaleData = Record<string, LocaleValue>;

let localeData: LocaleData = {};
let currentLocale = "en-US";

export class Locale extends LocaleBase {
  static override setLabelsOnce(localeDataParm: LocaleData) {
    if (localeData !== localeDataParm) {
      localeData = localeDataParm;
      const resources: Record<string, { translation: LocaleValue }> = {};
      Object.entries(localeData).forEach(([key, value]) => {
        if (Object.prototype.hasOwnProperty.call(localeData, key)) {
          resources[key] = {
            translation: value
          };
        }
      });
      if (!i18next.isInitialized) {
        i18next.init({
          lng: "en-US",
          debug: false,
          resources,
          interpolation: { prefix: "{", suffix: "}" }
        });
      } else {
        Object.entries(localeData).forEach(([locale, translations]) => {
          i18next.addResourceBundle(
            locale,
            "translation",
            translations,
            true,
            true
          );
        });
      }
      if (currentLocale !== "en-US") {
        Locale.change(currentLocale);
      }
    }
  }

  static override exists(locale: string) {
    return locale in localeData;
  }

  static override change(newLocale: string) {
    if (Locale.exists(newLocale)) {
      i18next.changeLanguage(newLocale);
      currentLocale = newLocale;
    }
    return currentLocale;
  }

  static override getCurrent() {
    return currentLocale;
  }

  static getPrice(value: number | string) {
    const val = typeof value !== "number" ? Number(value) : value;
    const locale = Locale.getCurrent();
    const currency =
      {
        "en-US": "USD",
        "en-CA": "CAD",
        "fr-CA": "CAD",
        "es-US": "USD"
      }[locale] || "USD";
    const formatter = new Intl.NumberFormat(Locale.getCurrent(), {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: val % 1 === 0 ? 0 : 2
    });
    return formatter.format(val);
  }

  static getAbbreviatedPrice(value: number | string, ismobile: boolean) {
    const numericValue = typeof value !== "number" ? Number(value) : value;
    let val = numericValue;
    const suffixes = ["", "K", "M"];
    let suffixNum = 0;

    if (val >= 1000000) {
      val = Math.trunc(val / 10);
      val /= 100000;
      suffixNum = 2;
    } else if (val >= 10000) {
      val = Math.trunc(val);
      val /= 1000;
      val = Math.round(val * 100) / 100;
      suffixNum = 1;
    }
    if (numericValue >= 999000 && numericValue <= 999999) {
      val = Math.round(val / 1000);
      suffixNum = 2;
    }
    if (ismobile) {
      val = Math.round(val);
    }
    const locale = Locale.getCurrent();
    const currency =
      {
        "en-US": "USD",
        "en-CA": "CAD",
        "fr-CA": "CAD",
        "es-US": "USD"
      }[locale] || "USD";
    const formatter = new Intl.NumberFormat(Locale.getCurrent(), {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: val % 1 === 0 ? 0 : 2
    });

    let formattedVal = formatter.format(val);
    const abbreviatedLetter = suffixes[suffixNum];

    if (locale === "fr-CA") {
      const indexBeforeDollarSign = formattedVal.length - 2;
      formattedVal = `${formattedVal.slice(
        0,
        indexBeforeDollarSign
      )}${abbreviatedLetter}${formattedVal.slice(indexBeforeDollarSign)}`;
    } else {
      formattedVal = `${formatter.format(val)}${abbreviatedLetter}`;
    }

    return formattedVal;
  }

  static getNumberFormat(value: number | string) {
    const val = typeof value !== "number" ? Number(value) : value;
    const formatter = new Intl.NumberFormat(Locale.getCurrent(), {});
    return formatter.format(val);
  }

  // convert date value in yyyy-mm-ddThh:mm to formatted locale date
  // toLocaleDateString only convert US date to m-d-yyyy when date and month is single digit while we want mm-dd-yyyy
  static getDateFormat(
    dateString: string | number | null | undefined,
    locale = "en-US"
  ) {
    if (!dateString) {
      return null;
    }
    const date = new Date(dateString);
    if (date.toString() === "Invalid Date") {
      return null;
    }
    if (locale.includes("US")) {
      const year = date.getFullYear();

      let month = (1 + date.getMonth()).toString();
      month = month.length > 1 ? month : `0${month}`;

      let day = date.getDate().toString();
      day = day.length > 1 ? day : `0${day}`;

      return `${month}/${day}/${year}`;
    }

    return date.toLocaleDateString(locale);
  }
}
